import { TriangleAlert } from "lucide-react"
import type * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Полоска сообщения: отказ ручки, предупреждение, подтверждение.
 *
 * Тон задаёт не только цвет, но и роль в разметке: `error` объявляется
 * `role="alert"` и зачитывается вслух сразу, остальное — `status`, то есть
 * «скажи, когда закончишь текущую фразу». Экранный диктор иначе перебивал бы
 * человека на каждом успешном сохранении.
 *
 * Красный тут — `decline`. В палитре консоли это цвет исхода скоринга, и вне
 * таблиц правил он занят только под настоящие отказы: если красным подсветить
 * ещё и подсказки, глаз перестанет отличать «не сохранилось» от «обратите
 * внимание».
 */
const TONES = {
  error: "border-decline/40 bg-decline/5 text-decline",
  warn: "border-review/40 bg-review/5 text-fg",
  ok: "border-approve/40 bg-approve/5 text-fg",
} as const

export function Notice({
  tone,
  icon: Icon = TriangleAlert,
  className,
  children,
}: {
  tone: keyof typeof TONES
  icon?: typeof TriangleAlert
  className?: string
  children: React.ReactNode
}) {
  return (
    <p
      role={tone === "error" ? "alert" : "status"}
      className={cn(
        "flex items-start gap-2 rounded border px-2.5 py-2 text-xs leading-relaxed",
        TONES[tone],
        className,
      )}
    >
      <Icon className="mt-px h-3.5 w-3.5 shrink-0" strokeWidth={2} />
      <span>{children}</span>
    </p>
  )
}
